import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

// The promises under the plan cards, as the website's pricing page shows
// them (voolist repo, src/sections/pricing/pricing-guarantee.jsx).

// One promise: a 44px tile, a title and one line
function TrustItem ({ tile, title, text, sx }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={sx}>
      {tile}
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.4 }}>{title}</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.4 }}>{text}</Typography>
      </Box>
    </Stack>
  );
}

TrustItem.propTypes = {
  sx: PropTypes.object,
  text: PropTypes.string,
  tile: PropTypes.node,
  title: PropTypes.string,
};

const tileSx = {
  width: 44,
  height: 44,
  flexShrink: 0,
  borderRadius: 1.5,
  display: 'grid',
  placeContent: 'center',
  textAlign: 'center',
};

// The money-back guarantee on its own, led by a "14 days" tile. The sign-up
// page shows it too.
export function MoneyBackPromise ({ sx }) {
  const theme = useTheme();

  return (
    <TrustItem
      sx={sx}
      tile={
        <Box
          aria-hidden
          sx={{
            ...tileSx,
            color: theme.palette.mode === 'light' ? 'primary.darker' : 'primary.light',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }}
        >
          <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.0625rem', lineHeight: 1 }}>
            14
          </Typography>
          <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.625rem', lineHeight: 1.2 }}>
            days
          </Typography>
        </Box>
      }
      title="Try Voolist for 14 days"
      text="Not happy? Get a full refund, no questions asked."
    />
  );
}

MoneyBackPromise.propTypes = {
  sx: PropTypes.object,
};

// Under the plan cards: the money-back guarantee and a safe checkout beside
// it. Side by side from sm, stacked on phones.
export default function PricingGuarantee ({ sx }) {
  const theme = useTheme();

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={{ xs: 2.5, sm: 6 }}
      sx={{ mx: 'auto', width: 'fit-content', ...sx }}
    >
      <MoneyBackPromise />

      <TrustItem
        tile={
          <Box
            aria-hidden
            sx={{
              ...tileSx,
              bgcolor: 'background.paper',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
            }}
          >
            <Iconify icon="solar:lock-keyhole-bold" width={22} sx={{ color: 'primary.main' }} />
          </Box>
        }
        title="Secure checkout"
        text="Cancel your plan anytime."
      />
    </Stack>
  );
}

PricingGuarantee.propTypes = {
  sx: PropTypes.object,
};
